import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * A render failure should say what happened.
 *
 * Without this, an exception during render unmounts the whole tree and leaves a
 * blank page — which on a phone is indistinguishable from a layout bug, a dead
 * bridge or a bad deploy. There is no console to check and no way to report
 * anything useful.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null; stack: string }> {
  state = { error: null as Error | null, stack: '' };

  static getDerivedStateFromError(error: Error) {
    return { error, stack: '' };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ error, stack: info.componentStack ?? '' });
    console.error('[render]', error, info.componentStack);
  }

  render() {
    const { error, stack } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="card" style={{ margin: 16 }}>
        <h2>Something failed to render</h2>
        <p className="small">
          The rest of the app is still running. Reload to try again, and send this message on if it keeps happening.
        </p>
        <pre style={{ whiteSpace: 'pre-wrap' }}>
          {error.name}: {error.message}
          {stack ? `\n${stack.split('\n').slice(0, 6).join('\n')}` : ''}
        </pre>
        <button className="primary" onClick={() => this.setState({ error: null, stack: '' })}>
          Try again
        </button>
      </div>
    );
  }
}
